import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit,
    Download,
    Printer,
    Trash2,
    User,
    FileText,
    DollarSign,
    Package,
    Mail,
    Phone,
    MapPin,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Copy,
    Send,
    Loader
} from 'lucide-react';
import { invoicesAPI } from '../../services/garageApi';
import { formatCurrency } from '../../utils/helpers';
import { ShoppingCart } from 'lucide-react';
import { InvoiceStatus } from '../../constants/enums';
import { sanitizeText, escapeHTML } from '../../utils/securityUtils';


const InvoiceDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');

    const fetchInvoice = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            const response = await invoicesAPI.show(id);
            const invoiceData = response.data;

            if (!invoiceData) {
                throw new Error('Invoice not found');
            }

            const formattedInvoice = {
                ...invoiceData,
                items: invoiceData.items && Array.isArray(invoiceData.items) ? invoiceData.items : [],
                subtotal: parseFloat(invoiceData.subtotal || 0),
                tax_amount: parseFloat(invoiceData.tax_amount || 0),
                discount_amount: parseFloat(invoiceData.discount_amount || 0),
                total: parseFloat(invoiceData.total || 0),
                tax_rate: parseFloat(invoiceData.tax_rate || 0)
            };

            setInvoice(formattedInvoice);
        } catch (err) {
            console.error('Error fetching invoice:', err);
            setError(sanitizeText(err.response?.data?.message || err.message || 'Failed to load invoice'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchInvoice();
    }, [fetchInvoice]);


    const handleDeleteInvoice = async () => {
        if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
            return;
        }

        try {
            await invoicesAPI.delete(id);
            alert('Invoice deleted successfully');
            navigate('/invoices');
        } catch (err) {
            console.error('Error deleting invoice:', err);
            const errorMsg = sanitizeText(err.response?.data?.message || err.message || 'Failed to delete invoice');
            alert('Error deleting invoice: ' + errorMsg);
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        // Validate status
        if (!InvoiceStatus.isValid(newStatus)) {
            alert('Invalid invoice status');
            return;
        }

        try {
            await invoicesAPI.updateStatus(id, { status: newStatus });
            setInvoice(prev => ({ ...prev, status: newStatus }));
            alert(`Invoice status updated to ${InvoiceStatus.labels()[newStatus]}`);
        } catch (err) {
            console.error('Error updating status:', err);
            const errorMsg = sanitizeText(err.response?.data?.message || err.message || 'Failed to update status');
            alert('Error updating status: ' + errorMsg);
        }
    };

    const handlePayment = async () => {
        const amount = parseFloat(paymentAmount);

        if (isNaN(amount) || amount <= 0) {
            alert('Please enter a valid payment amount');
            return;
        }

        if (amount > invoice.total) {
            alert('Payment amount cannot exceed invoice total');
            return;
        }

        try {
            // Note: This endpoint might not exist in your Laravel backend
            // Adjust the API call according to your backend implementation
            await invoicesAPI.addPayment?.(id, {
                amount: amount,
                payment_date: new Date().toISOString().split('T')[0]
            });

            alert('Payment recorded successfully');
            setShowPaymentForm(false);
            setPaymentAmount('');

            // Update invoice status based on payment amount
            if (amount >= invoice.total) {
                await handleUpdateStatus(InvoiceStatus.PAID);
            } else if (amount > 0) {
                await handleUpdateStatus(InvoiceStatus.PARTIAL);
            }
        } catch (err) {
            console.error('Error recording payment:', err);
            const errorMsg = sanitizeText(err.response?.data?.message || err.message || 'Failed to record payment');
            alert('Error recording payment: ' + errorMsg);
        }
    };

    const handleConvertToSale = async () => {
        if (!window.confirm('Convert this invoice to a sale? This will create a sale record from this invoice.')) {
            return;
        }

        try {
            const response = await invoicesAPI.convertToSale(id);
            const saleId = response?.data?.data?.id || response?.data?.id;
            alert('Invoice converted to sale successfully!');
            if (saleId) {
                navigate(`/sales/${saleId}`);
            } else {
                navigate('/sales');
            }
        } catch (err) {
            console.error('Error converting to sale:', err);
            const errorMsg = sanitizeText(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to convert invoice');
            alert('Error: ' + errorMsg);
        }
    };

    const printInvoice = () => {
        window.print();
    };

    const downloadInvoice = () => {
        // SECURITY FIX: Use safe HTML generation instead of innerHTML
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Please allow popups for this website to download invoices');
            return;
        }

        const invoiceNumber = escapeHTML(invoice.invoice_number || 'N/A');
        const customerName = escapeHTML(invoice.customer?.name || 'N/A');
        const invoiceDate = escapeHTML(invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A');
        const dueDate = escapeHTML(invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A');

        // Build items table safely
        const itemsRows = invoice.items?.map(item => {
            const productName = escapeHTML(item.product_name || item.name || 'Unknown Product');
            const quantity = parseFloat(item.quantity || 1);
            const unitPrice = parseFloat(item.unit_price || item.price || 0);
            const total = parseFloat(item.total || (quantity * unitPrice) || 0);

            return `
                <tr>
                    <td>${productName}</td>
                    <td>${quantity}</td>
                    <td>$${unitPrice.toFixed(2)}</td>
                    <td>$${total.toFixed(2)}</td>
                </tr>
            `;
        }).join('') || '<tr><td colspan="4">No items</td></tr>';

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Invoice #${invoiceNumber}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 30px; }
                    .invoice-details { margin-bottom: 20px; }
                    .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                    .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    .items-table th { background-color: #f5f5f5; }
                    .totals { margin-top: 20px; text-align: right; }
                    .total-row { font-weight: bold; font-size: 1.1em; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>INVOICE</h1>
                    <p>Invoice #${invoiceNumber}</p>
                </div>
                <div class="invoice-details">
                    <p><strong>Customer:</strong> ${customerName}</p>
                    <p><strong>Invoice Date:</strong> ${invoiceDate}</p>
                    <p><strong>Due Date:</strong> ${dueDate}</p>
                </div>
                <table class="items-table">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th>Qty</th>
                            <th>Unit Price</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsRows}
                    </tbody>
                </table>
                <div class="totals">
                    <p>Subtotal: $${invoice.subtotal.toFixed(2)}</p>
                    ${invoice.tax_amount > 0 ? `<p>Tax: $${invoice.tax_amount.toFixed(2)}</p>` : ''}
                    ${invoice.discount_amount > 0 ? `<p>Discount: -$${invoice.discount_amount.toFixed(2)}</p>` : ''}
                    <p class="total-row">Total: $${invoice.total.toFixed(2)}</p>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const copyInvoiceNumber = async () => {
        try {
            await navigator.clipboard.writeText(invoice.invoice_number);
            alert('Invoice number copied to clipboard');
        } catch (err) {
            console.error('Failed to copy:', err);
            alert('Failed to copy invoice number');
        }
    };

    const getStatusColor = (status) => {
        const normalizedStatus = status?.toLowerCase();
        return InvoiceStatus.colors()[normalizedStatus] || 'bg-gray-100 text-gray-800';
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid':
                return <CheckCircle className="h-4 w-4" />;
            case 'pending':
                return <Clock className="h-4 w-4" />;
            case 'overdue':
                return <XCircle className="h-4 w-4" />;
            case 'draft':
                return <AlertCircle className="h-4 w-4" />;
            default:
                return <AlertCircle className="h-4 w-4" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center">
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Loading invoice...
                </div>
            </div>
        );
    }

    if (error || !invoice) {
        return (
            <div className="space-y-4">
                <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">
                        {error || 'Invoice not found'}
                    </div>
                </div>
                <button
                    onClick={() => navigate('/invoices')}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Invoices
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => navigate('/invoices')}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                            Invoice #{invoice.invoice_number}
                            <button
                                onClick={copyInvoiceNumber}
                                className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                                title="Copy invoice number"
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </h1>
                        <p className="text-gray-600">
                            Created on {new Date(invoice.created_at).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span className="ml-2 capitalize">{invoice.status || 'draft'}</span>
                    </div>

                    <div className="flex space-x-2">
                        <button
                            onClick={downloadInvoice}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </button>
                        <button
                            onClick={() => navigate(`/invoices/${id}/edit`)}
                            className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                        </button>
                        <button
                            onClick={handleDeleteInvoice}
                            className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            {invoice.status !== 'paid' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-blue-900">Quick Actions</h3>
                            <p className="text-sm text-blue-700">Manage this invoice status and payments</p>
                        </div>
                        <div className="flex space-x-2">
                            {invoice.status === 'draft' && (
                                <button
                                    onClick={() => handleUpdateStatus('sent')}
                                    className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50"
                                >
                                    <Send className="h-4 w-4 mr-1" />
                                    Send Invoice
                                </button>
                            )}
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                <button
                                    onClick={handleConvertToSale}
                                    className="inline-flex items-center px-3 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-white hover:bg-green-50"
                                >
                                    <ShoppingCart className="h-4 w-4 mr-1" />
                                    Convert to Sale
                                </button>
                            )}
                            {(invoice.status === 'pending' || invoice.status === 'sent') && (
                                <button
                                    onClick={() => setShowPaymentForm(!showPaymentForm)}
                                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                >
                                    <DollarSign className="h-4 w-4 mr-2" />
                                    Record Payment
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Payment Form */}
                    {showPaymentForm && (
                        <div className="mt-4 p-4 bg-white rounded-lg border">
                            <h4 className="text-sm font-medium text-gray-900 mb-3">Record Payment</h4>
                            <div className="flex items-end space-x-3">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Payment Amount
                                    </label>
                                    <input
                                        type="number"
                                        value={paymentAmount}
                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                        placeholder={`Total: $${invoice.total.toFixed(2)}`}
                                        step="0.01"
                                        min="0"
                                        max={invoice.total}
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                                <button
                                    onClick={handlePayment}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                >
                                    Record
                                </button>
                                <button
                                    onClick={() => setShowPaymentForm(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Invoice Content */}
            <div id="invoice-content" className="bg-white shadow rounded-lg">
                {/* Invoice Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">INVOICE</h2>
                            <p className="text-sm text-gray-600 mt-1">Invoice #{invoice.invoice_number}</p>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-blue-600">Rubbert</div>
                            <p className="text-sm text-gray-600">Tire Management System</p>
                        </div>
                    </div>
                </div>

                {/* Billing Information */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Bill To */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <User className="h-4 w-4 mr-2" />
                                Bill To
                            </h3>
                            {invoice.customer ? (
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div className="font-medium text-gray-900">{invoice.customer.name}</div>
                                    {invoice.customer.email && (
                                        <div className="flex items-center">
                                            <Mail className="h-3 w-3 mr-2" />
                                            {invoice.customer.email}
                                        </div>
                                    )}
                                    {invoice.customer.phone && (
                                        <div className="flex items-center">
                                            <Phone className="h-3 w-3 mr-2" />
                                            {invoice.customer.phone}
                                        </div>
                                    )}
                                    {invoice.customer.address && (
                                        <div className="flex items-start">
                                            <MapPin className="h-3 w-3 mr-2 mt-0.5" />
                                            <span>{invoice.customer.address}</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">Customer information not available</div>
                            )}
                        </div>

                        {/* Invoice Details */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                <FileText className="h-4 w-4 mr-2" />
                                Invoice Details
                            </h3>
                            <div className="text-sm text-gray-600 space-y-2">
                                <div className="flex justify-between">
                                    <span>Invoice Date:</span>
                                    <span className="font-medium">
                    {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'Not set'}
                  </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Due Date:</span>
                                    <span className="font-medium">
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Not set'}
                  </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Status:</span>
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                    {invoice.status || 'draft'}
                  </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invoice Items */}
                <div className="px-6 py-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                        <Package className="h-4 w-4 mr-2" />
                        Items
                    </h3>

                    {invoice.items && invoice.items.length > 0 ? (
                        <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Qty
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Unit Price
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {invoice.items.map((item, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm text-gray-900">
                                            <div className="font-medium">{item.product_name || item.name || 'Unknown Product'}</div>
                                            {item.product_type && (
                                                <div className="text-xs text-gray-500 mt-1 capitalize">
                                                    Type: {item.product_type}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 text-center">
                                            {item.quantity || 1}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 text-right">
                                            {formatCurrency(parseFloat(item.unit_price || item.price || 0))}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 text-right font-medium">
                                            {formatCurrency(parseFloat(item.total || (Number(item.quantity) * Number(item.unit_price || item.price)) || 0))}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p>No items found for this invoice</p>
                        </div>
                    )}
                </div>

                {/* Invoice Totals */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-end">
                        <div className="w-64 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-medium">{formatCurrency(invoice.subtotal)}</span>
                            </div>

                            {invoice.tax_rate > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Tax ({invoice.tax_rate}%):</span>
                                    <span className="font-medium">{formatCurrency(invoice.tax_amount)}</span>
                                </div>
                            )}

                            {invoice.discount_amount > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Discount:</span>
                                    <span className="font-medium text-red-600">-{formatCurrency(invoice.discount_amount)}</span>
                                </div>
                            )}

                            <div className="flex justify-between text-lg font-bold border-t pt-2">
                                <span>Total:</span>
                                <span className="text-blue-600">{formatCurrency(invoice.total)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Notes */}
                {invoice.notes && (
                    <div className="px-6 py-4 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Notes</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{invoice.notes}</p>
                    </div>
                )}

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="text-center text-xs text-gray-500">
                        <p>Thank you for your business!</p>
                        <p className="mt-1">This invoice was generated on {new Date().toLocaleDateString()}</p>
                    </div>
                </div>
            </div>

            {/* Additional Actions */}
            <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <button
                        onClick={() => navigate(`/invoices/${id}/edit`)}
                        className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Invoice
                    </button>

                    <button
                        onClick={printInvoice}
                        className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        <Printer className="h-4 w-4 mr-2" />
                        Print Invoice
                    </button>

                    <button
                        onClick={() => {
                            const subject = encodeURIComponent(`Invoice ${sanitizeText(invoice.invoice_number)}`);
                            const body = encodeURIComponent(`Please find attached invoice ${sanitizeText(invoice.invoice_number)} for $${invoice.total.toFixed(2)}.`);
                            const email = sanitizeText(invoice.customer?.email || '');
                            const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;
                            window.location.href = mailtoLink;
                        }}
                        className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        disabled={!invoice.customer?.email}
                    >
                        <Send className="h-4 w-4 mr-2" />
                        Email Invoice
                    </button>

                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <button
                            onClick={handleConvertToSale}
                            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                        >
                            <ShoppingCart className="h-4 w-4 mr-2" />
                            Convert to Sale
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvoiceDetail;